import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ReplaceStrings from '../../config/replaceStrings';
import Routes from '../../config/routes';
import { MatchBundle } from './insightsQueries';
import {
    buildValorScoreRows,
    collectValorCounts,
    DEFAULT_VALORIZATION_WEIGHTS,
    formatValorPerGame,
    formatValorScore,
    loadValorWeights,
    saveValorWeights,
    VALOR_GROUP_LABELS,
    VALOR_WEIGHT_FIELDS,
    ValorizationWeights,
    ValorWeightGroup,
} from './insightsValorization';

interface Props {
    bundles: MatchBundle[];
    ourTeamIds: number[];
    visibleIds: Set<number>;
}

type SortKey = 'name' | 'matches' | 'total' | 'perGame';

const playerHref = (playerId: number): string =>
    Routes.ROUTE_INSIGHTS_PLAYER.replace(ReplaceStrings.PLAYER_ID, playerId.toString());

const GROUPS: ValorWeightGroup[] = ['goals', 'plus', 'minus', 'misses', 'gk'];

const InsightsValorizationBoard: React.FC<Props> = ({ bundles, ourTeamIds, visibleIds }) => {
    const [weights, setWeights] = useState<ValorizationWeights>(() => loadValorWeights());
    const [saved, setSaved] = useState(false);
    const [formEpoch, setFormEpoch] = useState(0);
    const [sortKey, setSortKey] = useState<SortKey>('total');
    const [sortDir, setSortDir] = useState<1 | -1>(-1);

    const counts = useMemo(() => collectValorCounts(bundles, ourTeamIds), [bundles, ourTeamIds]);
    const rows = useMemo(() => {
        const list = buildValorScoreRows(counts, weights, visibleIds);
        const sorted = [...list];
        sorted.sort((a, b) => {
            let cmp = 0;
            if (sortKey === 'name') cmp = a.name.localeCompare(b.name);
            else if (sortKey === 'matches') cmp = a.matches - b.matches;
            else if (sortKey === 'total') cmp = a.total - b.total;
            else cmp = a.perGame - b.perGame;
            if (cmp !== 0) return cmp * sortDir;
            return a.name.localeCompare(b.name);
        });
        return sorted;
    }, [counts, weights, visibleIds, sortKey, sortDir]);

    const onSort = (key: SortKey): void => {
        if (sortKey === key) {
            setSortDir((d) => (d === -1 ? 1 : -1));
            return;
        }
        setSortKey(key);
        setSortDir(key === 'name' ? 1 : -1);
    };

    const mark = (key: SortKey): string => {
        if (sortKey !== key) return '';
        return sortDir === -1 ? ' ↓' : ' ↑';
    };

    const setWeight = (key: keyof ValorizationWeights, value: number): void => {
        setWeights((prev) => ({ ...prev, [key]: value }));
        setSaved(false);
    };

    return (
        <section className="insightsPanel">
            <div className="insightsPanelHead">
                <h2>Valorization</h2>
                <span>Custom weights · live recalc</span>
            </div>

            <details className="insightsValorDetails">
                <summary>Weights</summary>
                <div className="insightsValorToolbar">
                    <button
                        type="button"
                        className="insightsZoneStatsToggle"
                        onClick={() => {
                            saveValorWeights(weights);
                            setSaved(true);
                        }}
                    >
                        {saved ? 'Saved' : 'Save weights'}
                    </button>
                    <button
                        type="button"
                        className="insightsZoneStatsToggle"
                        onClick={() => {
                            setWeights({ ...DEFAULT_VALORIZATION_WEIGHTS });
                            setFormEpoch((n) => n + 1);
                            setSaved(false);
                        }}
                    >
                        Reset defaults
                    </button>
                    <span className="insightsTableNote" style={{ padding: 0 }}>
                        Saved in this browser. Server templates stay separate.
                    </span>
                </div>
                {GROUPS.map((group) => (
                    <div key={group} className="insightsValorGroup">
                        <strong>{VALOR_GROUP_LABELS[group]}</strong>
                        <div className="insightsValorWeights">
                            {VALOR_WEIGHT_FIELDS.filter((f) => f.group === group).map((field) => (
                                <label key={field.key} className="insightsValorWeightRow">
                                    <span>{field.label}</span>
                                    <input
                                        key={`${formEpoch}-${field.key}`}
                                        type="number"
                                        step="0.1"
                                        defaultValue={weights[field.key]}
                                        onChange={(e) => {
                                            const next = Number(e.target.value);
                                            if (!Number.isFinite(next)) return;
                                            setWeight(field.key, next);
                                        }}
                                    />
                                </label>
                            ))}
                        </div>
                    </div>
                ))}
            </details>

            {rows.length === 0 ? (
                <div className="insightsEmpty">No players in this filter.</div>
            ) : (
                <div className="insightsTableScroll">
                    <table className="insightsPlayerTable insightsTwTable">
                        <thead>
                            <tr>
                                <th>
                                    <button
                                        type="button"
                                        className="insightsSortTh"
                                        onClick={() => onSort('name')}
                                    >
                                        Player{mark('name')}
                                    </button>
                                </th>
                                <th>
                                    <button
                                        type="button"
                                        className="insightsSortTh"
                                        onClick={() => onSort('matches')}
                                    >
                                        MP{mark('matches')}
                                    </button>
                                </th>
                                <th>
                                    <button
                                        type="button"
                                        className="insightsSortTh"
                                        onClick={() => onSort('total')}
                                    >
                                        Val{mark('total')}
                                    </button>
                                </th>
                                <th>
                                    <button
                                        type="button"
                                        className="insightsSortTh"
                                        onClick={() => onSort('perGame')}
                                    >
                                        Val/G{mark('perGame')}
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.playerId}>
                                    <td>
                                        <Link
                                            className="insightsMatchLink"
                                            to={playerHref(row.playerId)}
                                        >
                                            {row.name}
                                        </Link>
                                    </td>
                                    <td>{row.matches}</td>
                                    <td>{formatValorScore(row.total)}</td>
                                    <td>{formatValorPerGame(row.perGame)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

export default InsightsValorizationBoard;
